import { Zone } from '@teetsh/app/src/types/zone';
import startOfDay from '@teetsh/app/src/utils/time/startOfDay';
import _some from 'lodash-es/some';
import isWithinInterval from '@teetsh/app/src/utils/time/isWithinInterval';
import isEqual from '@teetsh/app/src/utils/time/isEqual';
import isBefore from '@teetsh/app/src/utils/time/isBefore';
import nextMonday from '@teetsh/app/src/utils/time/nextMonday';
import nextSunday from '@teetsh/app/src/utils/time/nextSunday';
import startOfWeek from '@teetsh/app/src/utils/time/startOfWeek';
import endOfWeek from '@teetsh/app/src/utils/time/endOfWeek';
import getDaysBetween from '@teetsh/app/src/utils/time/getDaysBetween';
import endOfDay from '@teetsh/app/src/utils/time/endOfDay';
import subDays from '@teetsh/app/src/utils/time/subDays';
import { getScholarWeeksBetween } from 'utils/time/getScholarWeeksBetween';
import { getNearestDay } from 'utils/time/getNearestDay';

function doesDateBelongToPeriode(start: Date, end: Date, date: Date): boolean {
  if (isBefore(end, start)) {
    return false;
  }
  return isWithinInterval(startOfDay(date), { start, end }, false);
}

interface PeriodeAndWeek {
  periode: string;
  week: number;
  totalWeeks: number;
}

function getWeekNumberOfPeriode(
  from: Date,
  to: Date,
  date: Date,
  sundayStart: boolean,
): number {
  let iterator = from;
  let weekNumber = 1;
  if (isEqual(date, from)) {
    return 1;
  }
  while (isBefore(iterator, to)) {
    let nextMondayOrSunday = nextMonday(iterator);
    if (sundayStart) {
      nextMondayOrSunday = nextSunday(iterator);
    }
    if (
      isWithinInterval(
        date,
        {
          start: startOfDay(iterator),
          end: endOfDay(subDays(nextMondayOrSunday, 1)),
        },
        false,
      )
    ) {
      return weekNumber;
    }
    if (isEqual(date, nextMondayOrSunday)) {
      return weekNumber + 1;
    }
    weekNumber++;
    iterator = nextMondayOrSunday;
  }
  return 1;
}

export default function getPeriodeAndWeek(
  date: Date,
  zone?: Zone,
  sundayFirstDayOfWeek = false,
): PeriodeAndWeek | undefined {
  if (!zone) {
    return undefined;
  }
  const startDayWeek = startOfWeek(date, sundayFirstDayOfWeek);
  const endDayWeek = endOfWeek(date, sundayFirstDayOfWeek);
  const daysOfTheWeek = getDaysBetween(startDayWeek, endDayWeek);
  const matchingPeriodes = zone.periodes.filter(p => {
    if (p.from && p.to) {
      return _some(daysOfTheWeek, d =>
        doesDateBelongToPeriode(p.from as Date, p.to as Date, d),
      );
    }
    return false;
  });
  if (matchingPeriodes.length === 0) {
    const matchingHoliday = zone.holidays.find(holiday => {
      if (holiday.from && holiday.to) {
        return doesDateBelongToPeriode(holiday.from, holiday.to, date);
      }
      return false;
    });
    if (matchingHoliday) {
      const weekNumber = getWeekNumberOfPeriode(
        // Nearest monday
        getNearestDay(matchingHoliday.from as Date, 1),
        // Nearest friday
        getNearestDay(matchingHoliday.to as Date, 5),
        date,
        sundayFirstDayOfWeek,
      );
      if (!weekNumber) {
        return undefined;
      }

      const totalWeeks = getScholarWeeksBetween(
        // Nearest monday
        getNearestDay(matchingHoliday.from as Date, 1),
        // Nearest sunday
        getNearestDay(matchingHoliday.to as Date, 0),
      );
      return {
        periode: matchingHoliday.label,
        week: weekNumber,
        totalWeeks,
      };
    }
    return undefined;
  }
  const firstPeriode = matchingPeriodes[0];

  const weekNumber = getWeekNumberOfPeriode(
    firstPeriode.from as Date,
    firstPeriode.to as Date,
    date,
    sundayFirstDayOfWeek,
  );
  if (!weekNumber) {
    return undefined;
  }
  return {
    periode: firstPeriode.label,
    week: weekNumber,
    totalWeeks: getScholarWeeksBetween(
      firstPeriode.from as Date,
      firstPeriode.to as Date,
    ),
  };
}
